/**
 * Admin → Agent edit form (Phase 28.10).
 *
 * Any save here is structural (this form edits topology / tooling, not the
 * prompt — prompts are edited via /admin/prompts). The submit flow:
 *   1. Diff proposed vs current → always structural for non-prompt fields
 *   2. Show confirmation modal ("This will open a PR")
 *   3. POST/PUT → backend validates DAG + opens PR → return 202 + prUrl
 *   4. Toast the PR URL
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getAdminAgent,
  listAdminAgents,
  updateAdminAgent,
} from "../services/adminAgents.js";
import { useStore } from "../store/useStore.js";
import ErrorBubble from "../components/ErrorBubble.jsx";
import LoadingSkeleton from "../components/LoadingSkeleton.jsx";
import Modal from "../components/Modal.jsx";

const KNOWN_TOOLS = [
  "web_search",
  "search_project_documents",
  "read_project_document",
  "run_research_pipeline",
  "get_pipeline_status",
  "get_cached_outputs",
  "save_document",
  "update_document_section",
];

export default function AdminAgentsEdit() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const addToast = useStore((s) => s.addToast);

  const [agent, setAgent] = useState(null);
  const [allAgents, setAllAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [agentResp, allResp] = await Promise.all([
        getAdminAgent(agentId),
        listAdminAgents(),
      ]);
      const a = agentResp.agent;
      setAgent(a);
      setAllAgents(allResp.agents || []);
      setForm({
        name: a.name || "",
        dependsOn: a.dependsOn || [],
        outputKey: a.outputKey || "",
        maxTokens: a.maxTokens ?? 16384,
        toolAllowlist: a.toolAllowlist || [],
        enabled: Boolean(a.enabled),
      });
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    load();
  }, [load]);

  function toggleDep(depId) {
    setForm((f) => ({
      ...f,
      dependsOn: f.dependsOn.includes(depId)
        ? f.dependsOn.filter((d) => d !== depId)
        : [...f.dependsOn, depId],
    }));
  }

  function toggleTool(tool) {
    setForm((f) => ({
      ...f,
      toolAllowlist: f.toolAllowlist.includes(tool)
        ? f.toolAllowlist.filter((t) => t !== tool)
        : [...f.toolAllowlist, tool],
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setShowConfirm(true);
  }

  async function confirmSave() {
    setShowConfirm(false);
    setSaving(true);
    setError(null);
    try {
      const payload = { ...agent, ...form };
      const result = await updateAdminAgent(agentId, payload);
      if (result.prUrl) {
        addToast({ type: "info", message: `PR opened: ${result.prUrl}` });
        navigate("/admin/agents/pending-prs");
      } else {
        addToast({ type: "success", message: "Agent updated." });
        navigate("/admin/agents");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingSkeleton variant="line" />;
  if (!form) return <ErrorBubble message={error || "Agent not found"} />;

  const otherAgents = allAgents.filter((a) => a.agentId !== agentId);

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <h1>Edit Agent</h1>
        <code className="admin-muted">{agentId}</code>
      </div>

      <p className="admin-page__subtext">
        All changes here are structural and will open a GitHub PR for review.
        To edit the agent&apos;s prompt, use{" "}
        <a href="/admin/prompts">Prompts</a>.
      </p>

      {error && <ErrorBubble message={error} />}

      <form onSubmit={handleSubmit} className="admin-form">
        <label className="admin-form__field">
          <span>Name</span>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
          />
        </label>

        <label className="admin-form__field">
          <span>Agent ID</span>
          <input type="text" value={agentId} readOnly className="admin-form__readonly" />
        </label>

        <label className="admin-form__field">
          <span>Prompt S3 Key</span>
          <input
            type="text"
            value={agent.promptS3Key || ""}
            readOnly
            className="admin-form__readonly"
          />
        </label>

        <fieldset className="admin-form__fieldset">
          <legend>Depends On</legend>
          {otherAgents.length === 0 ? (
            <p className="admin-muted">No other agents available.</p>
          ) : (
            otherAgents.map((a) => (
              <label key={a.agentId} className="admin-form__checkbox">
                <input
                  type="checkbox"
                  checked={form.dependsOn.includes(a.agentId)}
                  onChange={() => toggleDep(a.agentId)}
                />
                {a.name} <code>{a.agentId}</code>
              </label>
            ))
          )}
        </fieldset>

        <label className="admin-form__field">
          <span>Output Key</span>
          <input
            type="text"
            value={form.outputKey}
            onChange={(e) => setForm((f) => ({ ...f, outputKey: e.target.value }))}
            required
          />
        </label>

        <label className="admin-form__field">
          <span>Max Tokens</span>
          <input
            type="number"
            value={form.maxTokens}
            min={256}
            max={65536}
            onChange={(e) =>
              setForm((f) => ({ ...f, maxTokens: Number(e.target.value) }))
            }
          />
        </label>

        <fieldset className="admin-form__fieldset">
          <legend>Tool Allowlist</legend>
          {KNOWN_TOOLS.map((tool) => (
            <label key={tool} className="admin-form__checkbox">
              <input
                type="checkbox"
                checked={form.toolAllowlist.includes(tool)}
                onChange={() => toggleTool(tool)}
              />
              {tool}
            </label>
          ))}
        </fieldset>

        <label className="admin-form__field admin-form__field--inline">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
          />
          <span>Enabled</span>
        </label>

        <div className="admin-form__actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Opening PR…" : "Open PR for review"}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate("/admin/agents")}
          >
            Cancel
          </button>
        </div>
      </form>

      {showConfirm && (
        <Modal onClose={() => setShowConfirm(false)}>
          <div className="modal__body">
            <h2>Open pull request?</h2>
            <p>
              This will open a pull request on the repository with the updated
              agent configuration. Changes take effect once the PR is merged and
              CI passes.
            </p>
            <div className="modal__actions">
              <button className="btn btn-primary" onClick={confirmSave}>
                Continue
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
